<script setup lang="ts">
import type { ICredit } from '~/components/entity/credit'
import { CurrencyIcon } from '~/components/ui/currency-icon'

interface Props {
  credit: ICredit
}

const props = defineProps<Props>()

// Check if currency store is loaded and all required data is available
const currencyStore = useCurrencyStore()
const isReady = computed(() => {
  return (
    currencyStore.loaded &&
    props.credit.rates &&
    props.credit.rates.length > 0 &&
    // Check if at least one currency is available for the first rate
    props.credit.rates.every((rate) => useCurrencyProp(rate.currency_id, 'title'))
  )
})
</script>

<template>
  <div v-if="isReady">
    <div class="overflow-x-auto rounded-lg">
      <table class="w-full border-separate border-spacing-0">
        <!-- Table header -->
        <thead class="bg-gray-100 text-left font-semibold">
          <tr>
            <th class="min-w-[150px] rounded-tl-lg border-l border-t border-gray-200 px-3 py-4">
              {{ $t('common.currency') }}
            </th>
            <th
              class="whitespace-nowrap border-l border-t border-gray-200 px-3 py-4 text-center md:text-right"
            >
              {{ $t('widget.credit_page_rates_table.min') }}
            </th>
            <th
              class="whitespace-nowrap border-l border-t border-gray-200 px-3 py-4 text-center md:text-right"
            >
              {{ $t('widget.credit_page_rates_table.max') }}
            </th>
            <th
              class="whitespace-nowrap border-l border-t border-gray-200 px-3 py-4 text-center md:text-right"
            >
              {{ $t('common.period_placeholder') }}
            </th>
            <th
              class="min-w-[150px] rounded-tr-lg border-l border-r border-t border-gray-200 px-3 py-4 text-center md:text-right"
            >
              {{ $t('widget.credit_page_rates_table.rate') }}
            </th>
          </tr>
        </thead>

        <tbody>
          <tr v-for="(item, index) in credit.rates" :key="item.id">
            <td
              :class="[
                {
                  'rounded-bl-lg':
                    !credit.rates.some((i) => i.effective_rate) && index === credit.rates.length - 1
                },
                'min-w-[150px] border-b border-l border-gray-200 px-3 py-4'
              ]"
            >
              <div class="flex gap-2">
                <CurrencyIcon
                  :code="useCurrencyProp(item.currency_id, 'code') || ''"
                  :title="useCurrencyProp(item.currency_id, 'title') || ''"
                  size="lg"
                  country
                />
                {{ useCurrencyProp(item.currency_id, 'title') }}
              </div>
            </td>

            <td
              class="whitespace-nowrap border-b border-l border-gray-200 px-3 py-4 text-center md:text-right"
            >
              <template v-if="item.amount_from">
                {{ useCurrencyFormat(item.amount_from, item.currency_id) }}
              </template>
              <template v-else>-</template>
            </td>

            <td
              class="whitespace-nowrap border-b border-l border-gray-200 px-3 py-4 text-center md:text-right"
            >
              <template v-if="item.amount_to">
                {{ useCurrencyFormat(item.amount_to, item.currency_id) }}
              </template>
              <template v-else>-</template>
            </td>

            <td
              class="whitespace-nowrap border-b border-l border-gray-200 px-3 py-4 text-center md:text-right"
            >
              <template v-if="item.period">{{ item.period }}</template>
              <template v-else>-</template>
            </td>

            <td
              :class="[
                {
                  'rounded-br-lg':
                    !credit.rates.some((i) => i.effective_rate) && index === credit.rates.length - 1
                },
                'min-w-[150px] border-b border-l border-r border-gray-200 px-3 py-4 text-center md:text-right'
              ]"
            >
              <span v-if="item.rate">{{ item.rate }}%</span>
              <span v-if="item.effective_rate"> / {{ item.effective_rate }}%*</span>
            </td>
          </tr>

          <tr v-if="credit.rates.some((i) => i.effective_rate)">
            <td
              class="rounded-bl-lg rounded-br-lg border-b border-l border-r border-gray-200 px-3 py-4 text-right text-gray-500"
              colspan="5"
            >
              {{ $t('widget.credit_page_rates_table.effective_rate_note') }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>

  <!-- Loading state: Show skeleton -->
  <div v-else>
    <!-- Table skeleton -->
    <div class="hidden overflow-hidden rounded-lg md:block">
      <table class="w-full border-separate border-spacing-0">
        <thead class="bg-gray-100 text-left font-semibold">
          <tr>
            <th class="min-w-[150px] rounded-tl-lg border-l border-t border-gray-200 px-3 py-4">
              {{ $t('common.currency') }}
            </th>
            <th class="whitespace-nowrap border-l border-t border-gray-200 px-3 py-4">
              {{ $t('widget.credit_page_rates_table.min') }}
            </th>
            <th class="whitespace-nowrap border-l border-t border-gray-200 px-3 py-4">
              {{ $t('widget.credit_page_rates_table.max') }}
            </th>
            <th class="whitespace-nowrap border-l border-t border-gray-200 px-3 py-4">
              {{ $t('common.period_placeholder') }}
            </th>
            <th
              class="min-w-[150px] rounded-tr-lg border-l border-r border-t border-gray-200 px-3 py-4"
            >
              {{ $t('widget.credit_page_rates_table.rate') }}
            </th>
          </tr>
        </thead>

        <tbody>
          <tr v-for="n in 3" :key="n">
            <td class="border-b border-l border-gray-200 px-3 py-4">
              <div class="flex items-center gap-2">
                <div class="size-7 animate-pulse rounded bg-gray-200" />
                <div class="h-4 w-16 animate-pulse rounded bg-gray-200" />
              </div>
            </td>
            <td class="border-b border-l border-gray-200 px-3 py-4 text-right">
              <div class="ml-auto h-4 w-12 animate-pulse rounded bg-gray-200" />
            </td>
            <td class="border-b border-l border-gray-200 px-3 py-4 text-right">
              <div class="ml-auto h-4 w-12 animate-pulse rounded bg-gray-200" />
            </td>
            <td class="border-b border-l border-gray-200 px-3 py-4">
              <div class="h-4 w-20 animate-pulse rounded bg-gray-200" />
            </td>
            <td class="border-b border-l border-r border-gray-200 px-3 py-4">
              <div class="h-4 w-16 animate-pulse rounded bg-gray-200" />
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Mobile cards skeleton -->
    <div class="flex flex-col gap-4 md:hidden">
      <div
        v-for="n in 3"
        :key="n"
        class="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm"
      >
        <div class="mb-2">
          <div class="h-5 w-20 animate-pulse rounded bg-gray-200" />
        </div>
        <div class="space-y-2">
          <div class="h-4 w-24 animate-pulse rounded bg-gray-200" />
          <div class="h-4 w-28 animate-pulse rounded bg-gray-200" />
          <div class="h-4 w-20 animate-pulse rounded bg-gray-200" />
          <div class="h-4 w-16 animate-pulse rounded bg-gray-200" />
        </div>
      </div>
    </div>
  </div>
</template>
